import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getCsrfToken, ApiError } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import { Card, Button, Badge, Field, inputClass } from '../components/ui.js';
import { Select, FormGrid, Textarea, QueryState, ErrorState } from '../components/form.js';

const CATEGORIES = ['general', 'g6pd', 'imaging', 'exam', 'lab', 'other'] as const;
type Category = (typeof CATEGORIES)[number];

interface ClinicalNote {
  id: string;
  noteDate: string;
  category: Category;
  title: string | null;
  text: string;
  visibleToFamily: boolean;
  createdAt: string;
  createdBy: string | null;
  signedBy: string | null;
}

function catTone(category: string): 'info' | 'warn' | 'danger' | 'ok' {
  if (category === 'g6pd') return 'warn';
  if (category === 'imaging') return 'info';
  if (category === 'lab') return 'ok';
  return 'info';
}

// No shared i18n key exists for the optional note-title input label, so use a
// tiny inline FR/EN map (per conventions: avoid adding new shared keys).
const TITLE_LABEL = { fr: 'Titre (optionnel)', en: 'Title (optional)' } as const;

export function ClinicalNotes() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['clinicalNotes'] });

  const q = useQuery({
    queryKey: ['clinicalNotes'],
    queryFn: () => api.get<{ notes: ClinicalNote[] }>('/clinical-notes'),
  });

  const notes = q.data?.notes ?? [];

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-800 mb-1">{t('notes.title')}</h1>
      <p className="text-slate-500 mb-4">{t('notes.subtitle')}</p>

      <EtfImport onSaved={invalidate} />
      <NoteForm onSaved={invalidate} />

      <Card title={t('notes.title')}>
        <QueryState
          isLoading={q.isLoading}
          isError={q.isError}
          error={q.error}
          errorFallback={t('common.error')}
          isEmpty={notes.length === 0}
          emptyMessage={t('notes.empty')}
        >
          <ul className="space-y-3">
            {notes.map((n) => (
              <NoteItem key={n.id} note={n} onChanged={invalidate} />
            ))}
          </ul>
        </QueryState>
      </Card>
    </div>
  );
}

function NoteForm({ onSaved }: { onSaved: () => void }) {
  const { t, locale } = useI18n();
  const lang = locale === 'en' ? 'en' : 'fr';
  const today = new Date().toISOString().slice(0, 10);
  const [category, setCategory] = useState<Category>('general');
  const [noteDate, setNoteDate] = useState(today);
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [visibleToFamily, setVisibleToFamily] = useState(true);
  const [saved, setSaved] = useState(false);

  const m = useMutation({
    mutationFn: () =>
      api.post('/clinical-notes', {
        noteDate,
        category,
        title: title.trim() || undefined,
        text: text.trim(),
        visibleToFamily,
      }),
    onSuccess: () => {
      onSaved();
      setCategory('general');
      setNoteDate(today);
      setTitle('');
      setText('');
      setVisibleToFamily(true);
      setSaved(true);
    },
  });

  return (
    <Card title={t('notes.add')}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setSaved(false);
          m.mutate();
        }}
      >
        <FormGrid>
          <Field label={t('notes.category')}>
            <Select value={category} onChange={(e) => setCategory(e.target.value as Category)}>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{t(`notes.cat.${c}`)}</option>
              ))}
            </Select>
          </Field>
          <Field label={t('notes.noteDate')}>
            <input
              className={inputClass}
              type="date"
              value={noteDate}
              onChange={(e) => setNoteDate(e.target.value)}
              required
            />
          </Field>
        </FormGrid>
        <Field label={TITLE_LABEL[lang]}>
          <input className={inputClass} value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label={t('notes.text')}>
          <Textarea
            rows={4}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('notes.placeholder')}
            required
          />
        </Field>
        <label className="flex items-center gap-2 text-sm text-slate-700 mb-3 cursor-pointer">
          <input type="checkbox" checked={visibleToFamily} onChange={(e) => setVisibleToFamily(e.target.checked)} />
          {t('notes.visibleToFamily')}
        </label>
        {m.isError && (
          <div className="mb-3">
            <ErrorState error={m.error} fallback={t('common.error')} />
          </div>
        )}
        {saved && !m.isPending && <div className="mb-3 text-sm text-green-700">{t('notes.saved')}</div>}
        <div className="flex gap-2">
          <Button type="submit" disabled={m.isPending || !text.trim()}>{t('common.save')}</Button>
        </div>
      </form>
    </Card>
  );
}

/**
 * Drop an ETF / imaging report → Claude proposes a DRAFT summary the clinician
 * reviews/edits → saved as an 'imaging' clinical note (family-visible by default).
 */
function EtfImport({ onSaved }: { onSaved: () => void }) {
  const { t } = useI18n();
  const today = new Date().toISOString().slice(0, 10);
  const [file, setFile] = useState<File | null>(null);
  const [findings, setFindings] = useState('');
  const [examDate, setExamDate] = useState(today);
  const [visibleToFamily, setVisibleToFamily] = useState(true);
  const [phase, setPhase] = useState<'pick' | 'review' | 'done'>('pick');
  const [aiNote, setAiNote] = useState<string | null>(null);

  const analyze = useMutation({
    mutationFn: async (f: File) => {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('kind', 'imaging_report');
      const res = await fetch('/api/attachments', { method: 'POST', credentials: 'include', headers: { 'x-csrf-token': getCsrfToken() ?? '' }, body: fd });
      const text = await res.text();
      const json = text ? JSON.parse(text) : undefined;
      if (!res.ok) throw new ApiError(res.status, json?.error?.code ?? 'upload_failed', json?.error?.message ?? res.statusText);
      const attachmentId: string = json.attachment.id;
      return api.post<{ summarized: boolean; findings?: string; examDate?: string }>('/imaging/extract', { attachmentId });
    },
    onSuccess: (r) => {
      setFindings(r.findings ?? '');
      if (r.examDate && /^\d{4}-\d{2}-\d{2}$/.test(r.examDate)) setExamDate(r.examDate);
      setAiNote(r.summarized && r.findings ? t('notes.draftReview') : t('notes.aiUnavailable'));
      setPhase('review');
    },
  });

  const save = useMutation({
    mutationFn: () =>
      api.post('/clinical-notes', { noteDate: examDate, category: 'imaging', text: findings.trim(), visibleToFamily }),
    onSuccess: () => {
      onSaved();
      setFile(null);
      setFindings('');
      setExamDate(today);
      setVisibleToFamily(true);
      setPhase('done');
    },
  });

  return (
    <Card title={t('notes.importEtf')}>
      <p className="text-sm text-slate-500 mb-1">{t('notes.importEtfHint')}</p>
      <p className="text-xs text-slate-400 mb-3">{t('notes.aiPrivacy')}</p>

      {phase !== 'review' && (
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="file"
            accept="application/pdf,image/*"
            onChange={(e) => { setFile(e.target.files?.[0] ?? null); setPhase('pick'); }}
            className="text-sm"
          />
          <Button type="button" disabled={!file || analyze.isPending} onClick={() => file && analyze.mutate(file)}>
            {analyze.isPending ? t('notes.analyzing') : t('notes.analyze')}
          </Button>
        </div>
      )}

      {analyze.isError && <div className="mt-2"><ErrorState error={analyze.error} fallback={t('common.error')} /></div>}
      {phase === 'done' && <div className="text-sm text-green-700">{t('notes.saved')}</div>}

      {phase === 'review' && (
        <div className="space-y-3">
          {aiNote && <p className="text-sm text-slate-500">{aiNote}</p>}
          <Field label={t('notes.noteDate')}>
            <input className={inputClass} type="date" value={examDate} onChange={(e) => setExamDate(e.target.value)} />
          </Field>
          <Field label={t('notes.text')}>
            <Textarea rows={5} value={findings} onChange={(e) => setFindings(e.target.value)} placeholder={t('notes.placeholder')} />
          </Field>
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" checked={visibleToFamily} onChange={(e) => setVisibleToFamily(e.target.checked)} />
            {t('notes.visibleToFamily')}
          </label>
          {save.isError && <ErrorState error={save.error} fallback={t('common.error')} />}
          <div className="flex gap-2">
            <Button type="button" disabled={save.isPending || !findings.trim()} onClick={() => save.mutate()}>{t('notes.saveAsNote')}</Button>
            <Button type="button" variant="secondary" onClick={() => { setPhase('pick'); setFile(null); setFindings(''); }}>{t('common.cancel')}</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function NoteItem({ note, onChanged }: { note: ClinicalNote; onChanged: () => void }) {
  const { t } = useI18n();

  const toggle = useMutation({
    mutationFn: () => api.put(`/clinical-notes/${note.id}`, { visibleToFamily: !note.visibleToFamily }),
    onSuccess: onChanged,
  });
  const del = useMutation({
    mutationFn: () => api.del(`/clinical-notes/${note.id}`),
    onSuccess: onChanged,
  });

  return (
    <li className="border border-slate-200 rounded-lg p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-slate-400">{note.noteDate}</span>
            <Badge tone={catTone(note.category)}>{t(`notes.cat.${note.category}`)}</Badge>
            <Badge tone={note.visibleToFamily ? 'ok' : 'warn'}>
              {note.visibleToFamily ? t('notes.sharedWithFamily') : t('notes.privateNote')}
            </Badge>
            {note.title && <span className="font-medium text-slate-800">{note.title}</span>}
          </div>
          <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{note.text}</p>
          {note.createdBy && (
            <p className="text-xs text-slate-400 mt-2">{t('notes.by', { who: note.createdBy })}</p>
          )}
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <Button variant="secondary" onClick={() => toggle.mutate()} disabled={toggle.isPending}>
            {note.visibleToFamily ? t('notes.hideFromFamily') : t('notes.shareWithFamily')}
          </Button>
          <Button variant="danger" onClick={() => del.mutate()} disabled={del.isPending}>
            {t('notes.delete')}
          </Button>
        </div>
      </div>
      {(del.isError || toggle.isError) && (
        <div className="mt-2">
          <ErrorState error={del.error ?? toggle.error} fallback={t('common.error')} />
        </div>
      )}
    </li>
  );
}
